import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'
import type { PriorityStatus, WorkItemKind, WorkItemRow, WorkItemSize, WorkItemStatus, WorkRequestBrief } from '@/lib/workBoard'

/**
 * Доска работы команды (/management/work).
 *
 * Все обращения — через SECURITY DEFINER RPC миграции 20260906120000. Гейты
 * серверные: читать и оставлять запрос может любой действующий сотрудник УК,
 * менять карточки — участник `work_board_members`, состав команды — супер-админ.
 * Здесь права не дублируются; `isWorkBoardMember()` нужен только чтобы спрятать
 * кнопки, которые сервер всё равно не пропустит.
 */

type RpcResult<T> = { data: T | null; error: unknown }
type RpcClient = {
  rpc: <T>(functionName: string, args?: Record<string, unknown>) => PromiseLike<RpcResult<T>>
}

function rpcClient(): RpcClient {
  return getSupabase() as unknown as RpcClient
}

async function call<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await withAuthRecovery(() => rpcClient().rpc<T>(fn, args))
  if (error) throw error
  return data as T
}

export interface WorkDirection {
  code: string
  title: string
  sort_order: number
  is_active: boolean
}

export interface WorkBoardMember {
  user_id: string
  display_name: string | null
  member_role: 'developer' | 'lead'
  is_active: boolean
}

export interface WorkItemEvent {
  id: number
  event_type: 'created' | 'status' | 'progress' | 'blocked' | 'unblocked' | 'assigned' | 'updated' | 'link' | 'comment'
  actor_name: string | null
  occurred_at: string
  note: string | null
  payload: Record<string, unknown>
}

export interface WorkItemLink {
  id: number
  kind: 'commit' | 'branch' | 'pr' | 'doc' | 'migration' | 'script' | 'other'
  ref: string
  title: string | null
  occurred_at: string | null
  author_login: string | null
}

export interface WorkItemChild {
  id: number
  title: string
  status: WorkItemStatus
  assignee_user_id: string | null
  assignee_name: string | null
}

export interface WorkItemDetail {
  item: {
    id: number
    title: string
    direction_code: string
    direction_title: string
    kind: WorkItemKind
    status: WorkItemStatus
    size: WorkItemSize | null
    request_text: string | null
    expected_result: string | null
    requester: string | null
    assignee_user_id: string | null
    assignee_name: string | null
    parent_id: number | null
    parent_title: string | null
    is_unplanned: boolean
    displaced_item_id: number | null
    displaced_item_title: string | null
    next_step: string | null
    next_review_at: string | null
    blocked_reason: string | null
    blocked_waiting_on: string | null
    blocked_since: string | null
    tech_notes: string | null
    created_by_name: string | null
    created_at: string
    updated_at: string
    started_at: string | null
    done_at: string | null
    priority_rank: number | null
    request_brief: WorkRequestBrief | null
    acceptor_user_id: string | null
    acceptor_name: string | null
    created_by_user_id: string | null
  }
  events: WorkItemEvent[]
  links: WorkItemLink[]
  children: WorkItemChild[]
  is_member: boolean
  /** Автор или принимающий, пока запрос в «Новых» / «Доработке»; команда — всегда. */
  can_edit_request: boolean
  /** Принимающий (без него — автор) или команда, пока карточка в «Приёмке заказчика». */
  can_review_acceptance: boolean
}

export interface WorkAcceptor {
  user_id: string
  display_name: string | null
  job_title: string | null
}

export type WorkBoardNotificationKind =
  | 'new_item'
  | 'comment'
  | 'request_rework'
  | 'acceptance_returned'

export interface WorkBoardNotification {
  notification_id: number
  item_id: number
  item_title: string
  kind: WorkBoardNotificationKind
  created_at: string
  note: string | null
  actor_name: string | null
}

export interface WorkBoardNotifications {
  unread_count: number
  items: WorkBoardNotification[]
}

export async function getWorkBoardNotifications(limit = 10): Promise<WorkBoardNotifications> {
  const payload = await call<Partial<WorkBoardNotifications>>('get_work_board_notifications', { p_limit: limit })
  return {
    unread_count: Number(payload?.unread_count ?? 0),
    items: Array.isArray(payload?.items) ? payload.items : [],
  }
}

export async function markWorkItemNotificationsRead(itemId: number): Promise<number> {
  return Number(await call<number>('mark_work_item_notifications_read', { p_item_id: itemId }) ?? 0)
}

export async function markAllWorkBoardNotificationsRead(): Promise<number> {
  return Number(await call<number>('mark_all_work_board_notifications_read') ?? 0)
}

export async function isWorkBoardMember(): Promise<boolean> {
  try {
    return (await call<boolean>('work_board_is_member')) === true
  } catch {
    return false
  }
}

export async function fetchWorkDirections(): Promise<WorkDirection[]> {
  return (await call<WorkDirection[]>('list_work_directions')) ?? []
}

export async function fetchWorkBoardMembers(): Promise<WorkBoardMember[]> {
  return (await call<WorkBoardMember[]>('list_work_board_members')) ?? []
}

/** Все открытые карточки + закрытые с даты `closedFrom` (ISO date). */
export async function fetchWorkItems(closedFrom: string | null): Promise<WorkItemRow[]> {
  return (await call<WorkItemRow[]>('list_work_items', { p_closed_from: closedFrom, p_limit: 1000 })) ?? []
}

export async function fetchWorkItem(itemId: number): Promise<WorkItemDetail> {
  const detail = await call<WorkItemDetail>('get_work_item', { p_item_id: itemId })
  if (!detail) throw new Error('Карточка не найдена')
  return detail
}

/** Кого можно выбрать принимающим результат: действующие advanced-сотрудники УК. */
export async function fetchWorkAcceptors(): Promise<WorkAcceptor[]> {
  return (await call<WorkAcceptor[]>('list_work_acceptors')) ?? []
}

export interface WorkRequestInput {
  title: string
  requestText?: string | null
  brief: WorkRequestBrief
  /** Обязателен для всех, кроме супер-админа (решает сервер). */
  acceptorUserId: string | null
  directionCode?: string | null
}

/**
 * Запрос с анкетой (docs/WORK_REQUEST_RULES.md). Доступен любому сотруднику УК;
 * автор — текущий пользователь, анкету проверяет сервер. Супер-админу анкета и
 * принимающий необязательны: пустая анкета уходит как `{}` и хранится как NULL.
 */
export async function createWorkRequest(input: WorkRequestInput): Promise<number> {
  return call<number>('create_work_request', {
    p_title: input.title.trim(),
    p_request_text: input.requestText?.trim() || null,
    p_brief: input.brief,
    p_acceptor_user_id: input.acceptorUserId || null,
    p_direction_code: input.directionCode || null,
  })
}

/**
 * Правка запроса автором / принимающим / командой / супер-админом (любая карточка);
 * `resubmit` возвращает из «Доработки» в «Новые». У супер-админа форма — целиком:
 * пустая анкета и пустой принимающий очищают поля, у остальных NULL = «не менять».
 */
export async function updateWorkRequest(itemId: number, input: WorkRequestInput, resubmit: boolean): Promise<void> {
  await call<null>('update_work_request', {
    p_item_id: itemId,
    p_title: input.title.trim(),
    p_request_text: input.requestText?.trim() ?? '',
    p_brief: input.brief,
    p_acceptor_user_id: input.acceptorUserId || null,
    p_direction_code: input.directionCode || null,
    p_resubmit: resubmit,
  })
}

/** Приёмка: принять → «Готово», вернуть с замечанием → «В работе» наверх. */
export async function reviewWorkItemAcceptance(itemId: number, accepted: boolean, note?: string | null): Promise<void> {
  await call<null>('review_work_item_acceptance', { p_item_id: itemId, p_accepted: accepted, p_note: note?.trim() || null })
}

/** Новый порядок всего блока; сервер отказывает, если состав блока уже изменился. */
export async function reorderWorkItems(status: PriorityStatus, ids: number[], movedItemId: number): Promise<void> {
  await call<null>('reorder_work_items', { p_status: status, p_ids: ids, p_moved_item_id: movedItemId })
}

export interface NewWorkItem {
  title: string
  directionCode: string
  kind: WorkItemKind
  status: WorkItemStatus
  size?: WorkItemSize | null
  assigneeUserId?: string | null
  requester?: string | null
  requestText?: string | null
  expectedResult?: string | null
  nextStep?: string | null
  parentId?: number | null
  isUnplanned?: boolean
  displacedItemId?: number | null
  techNotes?: string | null
  startedAt?: string | null
  doneAt?: string | null
  note?: string | null
}

/** Карточка участником команды, в том числе постфактум («зафиксировать сделанное»). */
export async function createWorkItem(input: NewWorkItem): Promise<number> {
  return call<number>('create_work_item', {
    p_title: input.title.trim(),
    p_direction_code: input.directionCode,
    p_kind: input.kind,
    p_status: input.status,
    p_size: input.size ?? null,
    p_assignee_user_id: input.assigneeUserId ?? null,
    p_requester: input.requester?.trim() || null,
    p_request_text: input.requestText?.trim() || null,
    p_expected_result: input.expectedResult?.trim() || null,
    p_next_step: input.nextStep?.trim() || null,
    p_parent_id: input.parentId ?? null,
    p_is_unplanned: input.isUnplanned ?? false,
    p_displaced_item_id: input.displacedItemId ?? null,
    p_tech_notes: input.techNotes?.trim() || null,
    p_started_at: input.startedAt ?? null,
    p_done_at: input.doneAt ?? null,
    p_note: input.note?.trim() || null,
  })
}

export type WorkItemPatch = Partial<{
  title: string
  direction_code: string
  kind: WorkItemKind
  size: WorkItemSize | null
  requester: string | null
  request_text: string | null
  expected_result: string | null
  next_step: string | null
  next_review_at: string | null
  parent_id: number | null
  is_unplanned: boolean
  displaced_item_id: number | null
  tech_notes: string | null
  assignee_user_id: string | null
}>

/** Только переданные ключи меняются; ключ со значением null очищает поле. */
export async function updateWorkItem(itemId: number, patch: WorkItemPatch): Promise<void> {
  await call<null>('update_work_item', { p_item_id: itemId, p_patch: patch })
}

export async function setWorkItemStatus(itemId: number, status: WorkItemStatus, note?: string | null): Promise<void> {
  await call<null>('set_work_item_status', { p_item_id: itemId, p_status: status, p_note: note?.trim() || null })
}

export async function addWorkItemProgress(itemId: number, note: string): Promise<number> {
  return call<number>('add_work_item_progress', { p_item_id: itemId, p_note: note.trim() })
}

export async function addWorkItemComment(itemId: number, note: string): Promise<number> {
  return call<number>('add_work_item_comment', { p_item_id: itemId, p_note: note.trim() })
}

export async function setWorkItemBlock(itemId: number, reason: string, waitingOn?: string | null): Promise<void> {
  await call<null>('set_work_item_block', { p_item_id: itemId, p_reason: reason.trim(), p_waiting_on: waitingOn?.trim() || null })
}

export async function clearWorkItemBlock(itemId: number, note?: string | null): Promise<void> {
  await call<null>('clear_work_item_block', { p_item_id: itemId, p_note: note?.trim() || null })
}

export async function linkWorkItemRef(
  itemId: number,
  kind: WorkItemLink['kind'],
  ref: string,
  title?: string | null
): Promise<number> {
  return call<number>('link_work_item_ref', {
    p_item_id: itemId,
    p_kind: kind,
    p_ref: ref.trim(),
    p_title: title?.trim() || null,
    p_occurred_at: null,
    p_author_login: null,
  })
}
